import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, CalendarRange, Globe, MapPin } from "lucide-react";

const MONTHS = [
  { v: 1, l: "Jan" }, { v: 2, l: "Fev" }, { v: 3, l: "Mar" }, { v: 4, l: "Abr" },
  { v: 5, l: "Mai" }, { v: 6, l: "Jun" }, { v: 7, l: "Jul" }, { v: 8, l: "Ago" },
  { v: 9, l: "Set" }, { v: 10, l: "Out" }, { v: 11, l: "Nov" }, { v: 12, l: "Dez" },
];

export default function Seasons() {
  const [seasons, setSeasons] = useState([]);
  const [zones, setZones] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/seasons"),
    api.get("/admin/zones?active=true"),
  ]).then(([s, z]) => { setSeasons(s.data); setZones(z.data); });
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    try { await api.patch(`/admin/seasons/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Apagar temporada "${s.name}"?`)) return;
    try { await api.delete(`/admin/seasons/${s.id}`); toast.success("Temporada removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const zoneLabel = (key) => zones.find((z) => z.key === key)?.label || key;

  return (
    <div className="p-10 max-w-[1400px]" data-testid="seasons-page">
      <PageHeader
        eyebrow="Pricing · Calendário"
        title="Temporadas"
        description="Defina períodos de época alta/baixa globais ou específicos a uma zona. A prioridade decide qual temporada vence em caso de sobreposição de datas."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="new-season-btn">
          <Plus className="w-4 h-4" /> Nova temporada
        </GoldButton>
      </PageHeader>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-200 label-caps text-left">
              <th className="p-4">Nome</th>
              <th className="p-4">Âmbito</th>
              <th className="p-4">Datas</th>
              <th className="p-4 text-right">Prioridade</th>
              <th className="p-4">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {seasons.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500 text-sm">Sem temporadas.</td></tr>
            )}
            {seasons.map((s) => (
              <tr key={s.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`season-row-${s.id}`}>
                <td className="p-4 text-[#0a0f1e] font-semibold flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-[#f59e0b]" /> {s.name}
                </td>
                <td className="p-4">
                  {s.scope === "global" ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-600"><Globe className="w-3 h-3" /> Global</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#f59e0b] font-semibold"><MapPin className="w-3 h-3" /> {zoneLabel(s.zone_key)}</span>
                  )}
                </td>
                <td className="p-4 text-xs text-slate-700">
                  {(s.date_ranges || []).map((r, i) => (
                    <div key={i} className="font-mono">
                      {String(r.start_day).padStart(2, "0")}/{String(r.start_month).padStart(2, "0")} → {String(r.end_day).padStart(2, "0")}/{String(r.end_month).padStart(2, "0")}
                    </div>
                  ))}
                </td>
                <td className="p-4 text-right font-mono text-[#0a0f1e]">{s.priority}</td>
                <td className="p-4">
                  <button onClick={() => toggle(s)} className={`px-2 py-1 text-[9px] uppercase font-bold border ${s.active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`} data-testid={`toggle-season-${s.id}`}>
                    {s.active ? "Activa" : "Inactiva"}
                  </button>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(s); setShow(true); }} className="text-slate-500 hover:text-[#f59e0b]" data-testid={`edit-season-${s.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(s)} className="text-slate-500 hover:text-red-500" data-testid={`del-season-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && <SeasonModal season={editing} zones={zones} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); load(); }} />}
    </div>
  );
}

function SeasonModal({ season, zones, onClose, onSaved }) {
  const isEdit = !!season;
  const [f, setF] = useState(season || {
    name: "", priority: 100, scope: "global", zone_key: "",
    date_ranges: [{ start_month: 6, start_day: 15, end_month: 9, end_day: 15 }],
  });
  const [saving, setSaving] = useState(false);

  const addRange = () => setF({ ...f, date_ranges: [...f.date_ranges, { start_month: 1, start_day: 1, end_month: 1, end_day: 7 }] });
  const rmRange = (i) => setF({ ...f, date_ranges: f.date_ranges.filter((_, idx) => idx !== i) });
  const updRange = (i, k, v) => setF({ ...f, date_ranges: f.date_ranges.map((r, idx) => idx === i ? { ...r, [k]: Number(v) || 1 } : r) });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f };
      if (payload.scope === "global") payload.zone_key = null;
      if (isEdit) {
        await api.patch(`/admin/seasons/${season.id}`, payload);
        toast.success("Temporada actualizada");
      } else {
        await api.post("/admin/seasons", payload);
        toast.success("Temporada criada");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="season-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl text-[#0a0f1e]">{isEdit ? "Editar temporada" : "Nova temporada"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Nome</label>
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="season-name" placeholder="Época Alta Verão" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Âmbito</label>
              <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className="input-dt w-full" data-testid="season-scope">
                <option value="global">Global (todas as zonas)</option>
                <option value="zone">Específica a uma zona</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Prioridade</label>
              <Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: Number(e.target.value) || 100 })} data-testid="season-priority" />
            </div>
          </div>
          {f.scope === "zone" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Zona</label>
              <select required value={f.zone_key || ""} onChange={(e) => setF({ ...f, zone_key: e.target.value })} className="input-dt w-full" data-testid="season-zone">
                <option value="">Selecione...</option>
                {zones.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
              </select>
            </div>
          )}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Intervalos de data</label>
              <button type="button" onClick={addRange} className="text-[10px] uppercase text-[#f59e0b] font-bold" data-testid="add-range">+ Intervalo</button>
            </div>
            <div className="space-y-2">
              {f.date_ranges.map((r, i) => (
                <div key={i} className="border border-slate-200 p-3 flex items-center gap-2" data-testid={`range-${i}`}>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">De</span>
                  <Input type="number" min="1" max="31" value={r.start_day} onChange={(e) => updRange(i, "start_day", e.target.value)} className="w-16" />
                  <select value={r.start_month} onChange={(e) => updRange(i, "start_month", e.target.value)} className="input-dt">
                    {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">até</span>
                  <Input type="number" min="1" max="31" value={r.end_day} onChange={(e) => updRange(i, "end_day", e.target.value)} className="w-16" />
                  <select value={r.end_month} onChange={(e) => updRange(i, "end_month", e.target.value)} className="input-dt">
                    {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  {f.date_ranges.length > 1 && (
                    <button type="button" onClick={() => rmRange(i)} className="text-red-500 hover:text-red-700 ml-auto" data-testid={`rm-range-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Intervalos que ultrapassam o fim do ano (ex: 20/Dez → 5/Jan) são suportados.
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={saving} className="flex-1 justify-center" data-testid="save-season">{saving ? "A guardar…" : "Guardar"}</GoldButton>
        </div>
      </form>
    </div>
  );
}
